/**
 * Cron Logs viewer.
 *
 * Last 100 cron runs, newest first. Each row exposes:
 *   - Job name + status icon
 *   - Started timestamp (local time)
 *   - Duration
 *   - Result summary (one line)
 *   - Expandable detail: full error message + parsed result_data JSON
 *
 * Used to debug "why did the cron fail" questions without having to crack
 * open D1 directly.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  FiArrowLeft,
  FiCheck,
  FiX,
  FiClock,
  FiChevronDown,
  FiRefreshCw,
} from "react-icons/fi";

interface CronLogEntry {
  id: string;
  jobName: string;
  trigger: "cron" | "manual";
  status: "running" | "success" | "error";
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  resultSummary: string | null;
  resultData: string | null;
  errorMessage: string | null;
}

const STATUS_STYLES: Record<CronLogEntry["status"], { icon: typeof FiCheck; bg: string; text: string }> = {
  success: { icon: FiCheck, bg: "bg-emerald-100", text: "text-emerald-700" },
  error: { icon: FiX, bg: "bg-red-100", text: "text-red-700" },
  running: { icon: FiClock, bg: "bg-amber-100", text: "text-amber-700" },
};

function formatRelative(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${min % 60}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function prettyJson(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function CronLogsPage() {
  const [logs, setLogs] = useState<CronLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [jobFilter, setJobFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | CronLogEntry["status"]>("all");

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/cron-logs", { credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as CronLogEntry[];
        setLogs(data);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    const id = setInterval(fetchLogs, 15_000);
    return () => clearInterval(id);
  }, [fetchLogs]);

  const jobNames = Array.from(new Set(logs.map(l => l.jobName))).sort();

  const filtered = logs.filter(l => {
    if (jobFilter !== "all" && l.jobName !== jobFilter) return false;
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    return true;
  });

  function toggle(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Link to="/admin/campaigns" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <FiArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
          </div>
          <h1 className="font-serif text-3xl font-medium text-slate-900 mt-1">Cron Logs</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Last 100 runs of every scheduled job. Click a row to see full error / result data.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs}>
          <FiRefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base">All runs</CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <select
                value={jobFilter}
                onChange={e => setJobFilter(e.target.value)}
                className="h-8 px-2 rounded-md border border-border bg-white"
              >
                <option value="all">All jobs</option>
                {jobNames.map(j => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                className="h-8 px-2 rounded-md border border-border bg-white"
              >
                <option value="all">All statuses</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="running">Running</option>
              </select>
              <span className="text-muted-foreground">{filtered.length} shown</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No runs match the current filters.</p>
          ) : (
            <div className="space-y-1">
              {filtered.map(log => {
                const style = STATUS_STYLES[log.status];
                const StatusIcon = style.icon;
                const isExpanded = expanded.has(log.id);
                const errorText = log.errorMessage || (log.status === "error" ? "No error message recorded" : null);
                const parsedJson = prettyJson(log.resultData);
                return (
                  <div key={log.id} className="rounded-lg border border-border bg-muted/30">
                    <button
                      type="button"
                      onClick={() => toggle(log.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/60 transition-colors text-left"
                    >
                      <div className={`p-1.5 rounded-md shrink-0 ${style.bg}`}>
                        <StatusIcon className={`w-3.5 h-3.5 ${style.text}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {log.jobName}
                          <span className="text-muted-foreground font-normal ml-2 text-xs">
                            {formatRelative(log.startedAt)} · {formatDuration(log.durationMs)} · {log.trigger}
                          </span>
                        </p>
                        <p className={`text-xs truncate ${log.status === "error" ? "text-red-700" : "text-muted-foreground"}`}>
                          {log.status === "error"
                            ? errorText || log.resultSummary || "Error (no details)"
                            : log.resultSummary || "(no summary)"}
                        </p>
                      </div>
                      <FiChevronDown
                        className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="border-t border-border/50 px-4 py-3 space-y-3 text-xs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Started</p>
                            <p className="font-mono">{new Date(log.startedAt).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Completed</p>
                            <p className="font-mono">{log.completedAt ? new Date(log.completedAt).toLocaleString() : "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Duration</p>
                            <p className="font-mono">{formatDuration(log.durationMs)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Trigger</p>
                            <p className="font-mono">{log.trigger}</p>
                          </div>
                        </div>

                        {log.resultSummary && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Summary</p>
                            <p className="font-mono text-foreground/80 whitespace-pre-wrap break-words">
                              {log.resultSummary}
                            </p>
                          </div>
                        )}

                        {errorText && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-red-700 mb-0.5">Error</p>
                            <p className="font-mono text-red-800 whitespace-pre-wrap break-words bg-red-50 border border-red-200 rounded px-3 py-2">
                              {errorText}
                            </p>
                          </div>
                        )}

                        {parsedJson && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Result data</p>
                            <pre className="font-mono text-[11px] bg-slate-50 border border-slate-200 rounded px-3 py-2 overflow-x-auto whitespace-pre">
                              {parsedJson}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
